import React, { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { licznikRynku } from '../../api/rynek'

/**
 * Licznik na ikonie Rynku w pasku (uwagi przeglądu F2, 2026-10-25). Konto firmowe: zaproszenia czekające na decyzję.
 * Klient: zlecenia z ofertami czekającymi na wybór wykonawcy. Jedno lekkie zapytanie `GET /rynek/licznik` przy zmianie
 * strony i powrocie do karty, bez pobierania list. Decyzje na tej samej stronie (odmowa, oferta, wycofanie, wybór
 * wykonawców, zamknięcie zlecenia) wysyłają z `api/rynek` zdarzenie `vv-rynek-odswiez`, więc licznik od razu się zmienia.
 */
export const useLicznikRynku = (wlaczony = true) => {
  const { isAuthenticated } = useAuth()
  const { pathname } = useLocation()
  const [liczba, setLiczba] = useState(0)
  const aktywny = wlaczony && isAuthenticated

  const odswiez = useCallback(async () => {
    const res = await licznikRynku()
    if (res.ok) setLiczba(Number(res.data?.liczba) || 0)
  }, [])

  useEffect(() => {
    if (!aktywny) { setLiczba(0); return undefined }
    odswiez()
    return undefined
  }, [aktywny, odswiez, pathname])

  useEffect(() => {
    if (!aktywny) return undefined
    const naFokus = () => { if (document.visibilityState === 'visible') odswiez() }
    document.addEventListener('visibilitychange', naFokus)
    window.addEventListener('vv-rynek-odswiez', odswiez)
    return () => {
      document.removeEventListener('visibilitychange', naFokus)
      window.removeEventListener('vv-rynek-odswiez', odswiez)
    }
  }, [aktywny, odswiez])

  return liczba
}

/** Nazwa dla czytnika ekranu: sama plakietka jest `aria-hidden`, więc liczbę niesie etykieta odnośnika. */
export const etykietaZLicznikiem = (etykieta, liczba) => (liczba > 0 ? `${etykieta}, do decyzji: ${liczba}` : etykieta)

/**
 * Czerwona plakietka wystająca poza obrys przycisku (rodzic musi mieć `relative`), najwyżej „9+”. Przy zerze nic.
 * Obwódka w kolorze paska (`--vv-bg-white`), bo `ring-white` nie ciemnieje w ciemnym motywie.
 */
const PlakietkaRynku = ({ liczba }) => (liczba > 0 ? (
  <span aria-hidden="true" data-testid="licznik-rynku"
        className="pointer-events-none absolute -right-1.5 -top-1.5 z-20 flex h-[18px] min-w-[18px] items-center justify-center
                   rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-[rgb(var(--vv-bg-white))]">
    {liczba > 9 ? '9+' : liczba}
  </span>
) : null)

export default PlakietkaRynku
